import React, { useEffect, useRef } from "react";
import { ord, num, placeLabel, roundsOf, roundOf, markOf, isSingleJudge, hasRecall, placedOf, sectionOf, SECTION_LABEL, statusOf } from "./oireachtasResultsModel";
import "./oireachtasResultsTable.css";

// The full results table on View Scores' Results tab (Devin 2026-10-28: "the
// full table as we would in public results"). A copy of the web app's
// src/Parent/Results/OireachtasResultBlocks.jsx FullTable (and the pieces it
// uses): keep the two alike. Render it inside an element with the classes
// "oir er" (the styles are scoped like the web page's). mineCards / seats
// highlight a viewer's own dancers or judge columns there; here they are empty.

const Msr = ({ children, className = "" }) => <span className={`msr ${className}`} aria-hidden="true">{children}</span>;

// Place pill: medal + 1st/2nd/3rd in gold / silver / bronze, "3T" on a tie.
export const PlacePill = ({ position, tied }) => (
  <span className={`er-place ${position <= 3 ? `p${position}` : ""}`} title={tied ? "Tied" : undefined}>
    <Msr>workspace_premium</Msr>
    {placeLabel(position, tied)}
    {tied && <span className="er-sr-only"> (tied)</span>}
  </span>
);

// The full published table (Devin 2026-10-27): the feis championship table
// blended with the official results sheets. Place (card number under it),
// dancer (school under it), total Irish points and status (WQ / NQ) first,
// place and dancer pinned; then each round's marks as score/IP with the
// round total, that judge's place for the dancer under each mark. Counted
// marks bold, the marks the high/low drop removed in grey, green lines
// between rounds, green totals, every other row striped (as on the print).
// With a recall every recalled dancer is placed, so the only divider is
// "Not recalled" ("Did not place" without a recall).
// Rows don't open anything (Devin 2026-10-27). A judge's own seats are highlighted,
// and the table opens scrolled to the first of them.
export const FullTable = ({ detail, mineCards, seats }) => {
  if (isSingleJudge(detail)) return <SingleJudgeTable detail={detail} mineCards={mineCards} seats={seats} />;
  return <PanelTable detail={detail} mineCards={mineCards} seats={seats} />;
};

// One judge, one round: Place | Dancer | Score (the judge's raw score; no
// Irish points, as on the feis single-judge table) | Status.
const SingleJudgeTable = ({ detail, mineCards, seats }) => {
  const [{ round, judges }] = roundsOf(detail);
  const judge = judges[0];
  const mine = seats?.has(`${round}-${judge.slot}`);
  const showStatus = detail.rows.some((r) => statusOf(r).length > 0);
  let lastSection = "placed";
  return (
    <>
      <div className="er-rt-head">
        <span className="er-note">{detail.rows.length} dancers{detail.rows.some((r) => r.placed) ? ` · ${detail.rows.filter((r) => r.placed).length} placed` : ""}</span>
        <span className="er-judge1">Judge: <b>{judge.name || judge.slot}</b></span>
      </div>
      <div className="or-tw" tabIndex={0} role="region" aria-label="Results table (scrolls sideways)">
        <table className="or-table single">
          <thead>
            <tr>
              <th scope="col" className="or-c-place or-pin">Place</th>
              <th scope="col" className="or-c-name or-pin">Dancer</th>
              <th scope="col" className={`or-c-score${mine ? " me-col" : ""}`}>Score{mine && <span className="er-sr-only"> (your marks)</span>}</th>
              {showStatus && <th scope="col" className="or-c-status">Status</th>}
            </tr>
          </thead>
          <tbody>
            {detail.rows.map((r, i) => {
              const section = sectionOf(r, false);
              const divider = section !== lastSection && section !== "placed" ? SECTION_LABEL[section] : null;
              lastSection = section;
              const m = markOf(r, round, judge.slot);
              const rr = roundOf(r, round);
              const status = statusOf(r);
              return (
                <React.Fragment key={r.card}>
                  {divider && <tr className="or-divider"><td colSpan={showStatus ? 4 : 3}><span>{divider}</span></td></tr>}
                  <tr className={[mineCards.has(r.card) ? "me" : "", r.placed ? "" : "unplaced", i % 2 ? "alt" : ""].filter(Boolean).join(" ")}>
                    <td className="or-c-place or-pin">
                      {r.placed ? <PlacePill position={r.position} tied={r.tied} />
                        : <span className="er-place plain"><Msr className="slot">workspace_premium</Msr>{r.position ? placeLabel(r.position, r.tied) : ""}</span>}
                      <div className="or-cardno">#{r.card}</div>
                    </td>
                    <td className="or-c-name or-pin"><div className="or-nm">{r.name}</div><div className="or-sc">{r.school}</div></td>
                    <td className={`or-c-score${mine ? " me-col" : ""}`}>{rr?.noShow ? "No show" : m ? num(m.score) : ""}</td>
                    {showStatus && <td className="or-c-status">{status.map((t) => <span key={t} className="or-st">{t}</span>)}</td>}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {(showStatus || detail.rows.some((r) => r.tied)) && (
        <p className="or-legend">T = tied{showStatus ? " · WM = World medal holder · WQ = World qualifier · NQ = NAIDC qualifier · FTC = Failed to complete" : ""}</p>
      )}
    </>
  );
};

const PanelTable = ({ detail, mineCards, seats }) => {
  const ref = useRef(null);
  const rounds = roundsOf(detail);
  const multi = hasRecall(detail);
  const star = detail.competition.dropHighLow ? "*" : "";
  const seatClass = (round, slot) => (seats?.has(`${round}-${slot}`) ? " me-col" : "");
  // the Status column only when some dancer here has a status (Devin 2026-10-27)
  const showStatus = detail.rows.some((r) => statusOf(r).length > 0);
  const span = (showStatus ? 4 : 3) + rounds.reduce((n, { judges }) => n + judges.length + 1, 0);

  // once per competition (not on every minute refresh, which would pull a
  // judge back while they read another round)
  const scrolledFor = useRef(null);
  const compKey = String(detail.competition?._id || detail.competition?.code || "");
  useEffect(() => {
    const wrap = ref.current;
    const cell = wrap?.querySelector("th.me-col");
    if (!wrap || !cell || scrolledFor.current === compKey) return;
    scrolledFor.current = compKey;
    // place and dancer stay pinned: bring the judge's first column in beside them
    const pinned = [...wrap.querySelectorAll("thead tr:first-child th.or-pin")].reduce((w, el) => w + el.offsetWidth, 0);
    wrap.scrollLeft = Math.max(0, cell.offsetLeft - pinned - 8);
  }, [seats, compKey]);

  let lastSection = "placed";
  return (
    <>
      <div className="er-rt-head">
        <span className="er-note">{detail.rows.length} dancers{detail.rows.some((r) => placedOf(r, multi)) ? ` · ${detail.rows.filter((r) => placedOf(r, multi)).length} placed` : ""}</span>
        <span className="er-scroll-hint">Swipe for the rounds<Msr>arrow_forward</Msr></span>
      </div>
      <div ref={ref} className="or-tw" tabIndex={0} role="region" aria-label="Results table (scrolls sideways)">
        <table className="or-table">
          <thead>
            <tr>
              <th scope="col" rowSpan={2} className="or-c-place or-pin">Place</th>
              <th scope="col" rowSpan={2} className="or-c-name or-pin">Dancer</th>
              <th scope="col" rowSpan={2} className="or-c-total">Total IP</th>
              {showStatus && <th scope="col" rowSpan={2} className="or-c-status">Status</th>}
              {rounds.map(({ round, judges }) => (
                <th key={round} scope="colgroup" colSpan={judges.length + 1} className="or-c-round">Round {round} <span>· Score/IP</span></th>
              ))}
            </tr>
            <tr className="or-judges">
              {rounds.map(({ round, judges }) => (
                <React.Fragment key={round}>
                  {judges.map((j, k) => (
                    <th key={j.slot} scope="col" className={`or-c-j${k === 0 ? " or-rs" : ""}${seatClass(round, j.slot)}`} title={j.name}>
                      {j.name || j.slot}{seats?.has(`${round}-${j.slot}`) && <span className="er-sr-only"> (your marks)</span>}
                    </th>
                  ))}
                  <th scope="col" className="or-c-rt">Total{star}</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {detail.rows.map((r, i) => {
              const section = sectionOf(r, multi);
              const divider = section !== lastSection && section !== "placed" ? SECTION_LABEL[section] : null;
              lastSection = section;
              const status = statusOf(r);
              return (
                <React.Fragment key={r.card}>
                  {divider && (
                    <tr className="or-divider"><td colSpan={span}><span>{divider}</span></td></tr>
                  )}
                  <tr className={[mineCards.has(r.card) ? "me" : "", placedOf(r, multi) ? "" : "unplaced", i % 2 ? "alt" : ""].filter(Boolean).join(" ")}>
                    <td className="or-c-place or-pin">
                      {placedOf(r, multi) ? <PlacePill position={r.position} tied={r.tied} />
                        : <span className="er-place plain"><Msr className="slot">workspace_premium</Msr>{r.position ? placeLabel(r.position, r.tied) : ""}</span>}
                      <div className="or-cardno">#{r.card}</div>
                    </td>
                    <td className="or-c-name or-pin"><div className="or-nm">{r.name}</div><div className="or-sc">{r.school}</div></td>
                    <td className="or-c-total">{num(r.totalPoints)}</td>
                    {showStatus && <td className="or-c-status">{status.map((t) => <span key={t} className="or-st">{t}</span>)}</td>}
                    {rounds.map(({ round, judges }) => {
                      const rr = roundOf(r, round);
                      return (
                        <React.Fragment key={round}>
                          {judges.map((j, k) => {
                            const m = markOf(r, round, j.slot);
                            return (
                              <td key={j.slot} className={`or-c-j${k === 0 ? " or-rs" : ""}${m?.dropped ? " or-drop" : ""}${seatClass(round, j.slot)}`}>
                                {rr && !rr.noShow && m ? <>{num(m.score)}/{num(m.points)}{m.rank ? <small><span className="er-sr-only">, place </span>{ord(m.rank)}</small> : null}{m.dropped && <span className="er-sr-only"> (dropped)</span>}</> : ""}
                              </td>
                            );
                          })}
                          <td className="or-c-rt">{rr ? (rr.noShow ? "NS" : num(rr.total)) : ""}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="or-legend">
        T = tied · WM = World medal holder · WQ = World qualifier · NQ = NAIDC qualifier · FTC = Failed to complete · Score/IP = the judge&apos;s mark / Irish points, with that judge&apos;s place under it.{star ? " * Round totals leave out each dancer's high and low Irish points (in grey)." : ""}
      </p>
    </>
  );
};
